import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection } from '../common';

// Chore Form Dialog
interface ChoreData {
  name: string;
  description?: string;
  points?: number;
  frequency?: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'asNeeded';
  notes?: string;
  completed?: boolean;
  assignedTo?: string[];
  member?: string;
  member_id?: string;
}

interface ChoreFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ChoreData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  chore?: { id: string; data?: { data?: ChoreData } } | null;
}

export const ChoreFormDialog: React.FC<ChoreFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  chore,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!chore;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [points, setPoints] = useState('');
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'biweekly' | 'monthly' | 'asNeeded'>('daily');
  const [notes, setNotes] = useState('');
  const [completed, setCompleted] = useState(false);
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (chore) {
        const data = (chore.data?.data || {}) as any;
        setName(data.name || '');
        setDescription(data.description || '');
        setPoints(data.points?.toString() || '');
        setFrequency(data.frequency || 'daily');
        setNotes(data.notes || '');
        setCompleted(data.completed || false);
        // Support both data models
        if (data.assignedTo && data.assignedTo.length > 0) {
          setAssignedTo(data.assignedTo);
        } else if (data.member_id) {
          setAssignedTo([data.member_id]);
        } else {
          setAssignedTo([]);
        }
      } else {
        setName('');
        setDescription('');
        setPoints('');
        setFrequency('daily');
        setNotes('');
        setCompleted(false);
        setAssignedTo([]);
      }
      setError(null);
    }
  }, [open, chore]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Chore name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Write both data models for cross-platform compatibility
      const firstMemberId = assignedTo[0];
      const firstMemberName = firstMemberId ? getMemberName(firstMemberId) : undefined;

      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        points: points ? parseInt(points) : undefined,
        frequency,
        notes: notes.trim() || undefined,
        completed,
        assignedTo: assignedTo.length > 0 ? assignedTo : undefined,
        member: firstMemberName || undefined,
        member_id: firstMemberId || undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save chore');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete chore');
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (memberId: string) => {
    setAssignedTo((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Chore' : 'New Chore'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField label="Chore Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus placeholder="e.g., Make bed, Clean room" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Points" value={points} onChange={(e) => setPoints(e.target.value)} fullWidth type="number" placeholder="0" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select value={frequency} label="Frequency" onChange={(e) => setFrequency(e.target.value as any)}>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="biweekly">Bi-Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="asNeeded">As Needed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>

          {isEditing && (
            <Grid size={12}>
              <FormControlLabel
                control={<Switch checked={completed} onChange={(e) => setCompleted(e.target.checked)} />}
                label="Completed"
              />
            </Grid>
          )}

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Assigned To</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberList.map((member) => {
                const isSelected = assignedTo.includes(member.id);
                const memberName = getMemberName(member.id);
                const color = getMemberColor(member.id);
                return (
                  <Chip
                    key={member.id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{memberName.charAt(0)}</Avatar>}
                    label={memberName}
                    onClick={() => toggleMember(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Routine Form Dialog
const ROUTINE_TYPES = [
  { key: 'morning', label: 'Morning', color: '#F59E0B' },
  { key: 'afternoon', label: 'Afternoon', color: '#F97316' },
  { key: 'bedtime', label: 'Bedtime', color: '#6366F1' },
  { key: 'custom', label: 'Custom', color: '#6B7280' },
];

const DAYS = [
  { key: 'Mon', label: 'Mon' },
  { key: 'Tue', label: 'Tue' },
  { key: 'Wed', label: 'Wed' },
  { key: 'Thu', label: 'Thu' },
  { key: 'Fri', label: 'Fri' },
  { key: 'Sat', label: 'Sat' },
  { key: 'Sun', label: 'Sun' },
];

interface TaskItem {
  id: string;
  name: string;
  completed: boolean;
}

interface RoutineData {
  name: string;
  type?: string;
  description?: string;
  time?: string;
  days?: string[];
  tasks?: TaskItem[];
  assignedTo?: string[];
  member?: string;
  member_id?: string;
}

interface RoutineFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: RoutineData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  routine?: { id: string; data?: { data?: RoutineData } } | null;
}

export const RoutineFormDialog: React.FC<RoutineFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  routine,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!routine;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [time, setTime] = useState('');
  const [days, setDays] = useState<string[]>([]);
  const [tasks, setTasks] = useState<TaskItem[]>([{ id: `task_${Date.now()}`, name: '', completed: false }]);
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (routine) {
        const data = (routine.data?.data || {}) as any;
        setName(data.name || '');
        setType(data.type || '');
        setDescription(data.description || '');
        setTime(data.time || data.start_time || '');
        setDays(data.days || []);
        setTasks(data.tasks && data.tasks.length > 0
          ? data.tasks
          : [{ id: `task_${Date.now()}`, name: '', completed: false }]);
        if (data.assignedTo && data.assignedTo.length > 0) {
          setAssignedTo(data.assignedTo);
        } else if (data.member_id) {
          setAssignedTo([data.member_id]);
        } else {
          setAssignedTo([]);
        }
      } else {
        setName('');
        setType('');
        setDescription('');
        setTime('');
        setDays([]);
        setTasks([{ id: `task_${Date.now()}`, name: '', completed: false }]);
        setAssignedTo([]);
      }
      setError(null);
    }
  }, [open, routine]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Routine name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const firstMemberId = assignedTo[0];
      const firstMemberName = firstMemberId ? getMemberName(firstMemberId) : undefined;
      const filteredTasks = tasks.filter((t) => t.name.trim());

      await onSave({
        name: name.trim(),
        type: type || undefined,
        description: description.trim() || undefined,
        time: time || undefined,
        days: days.length > 0 ? days : undefined,
        tasks: filteredTasks.length > 0 ? filteredTasks : undefined,
        assignedTo: assignedTo.length > 0 ? assignedTo : undefined,
        member: firstMemberName || undefined,
        member_id: firstMemberId || undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save routine');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete routine');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: string) => {
    setDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const toggleMember = (memberId: string) => {
    setAssignedTo((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  const addTask = () => {
    setTasks((prev) => [...prev, { id: `task_${Date.now()}`, name: '', completed: false }]);
  };

  const removeTask = (taskId: string) => {
    if (tasks.length <= 1) return;
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  };

  const updateTaskName = (taskId: string, name: string) => {
    setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, name } : t));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Routine' : 'New Routine'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Routine Type */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Type</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ROUTINE_TYPES.map((rt) => (
                <Chip
                  key={rt.key}
                  label={rt.label}
                  onClick={() => setType(type === rt.key ? '' : rt.key)}
                  variant={type === rt.key ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    ...(type === rt.key && { bgcolor: rt.color, color: '#fff', '&:hover': { bgcolor: rt.color } }),
                  }}
                />
              ))}
            </Box>
          </Grid>

          <Grid size={12}>
            <TextField label="Routine Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus placeholder="e.g., Morning Routine, Bedtime Routine" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={12}>
            <TextField label="Time" type="time" value={time} onChange={(e) => setTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Days</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DAYS.map((day) => (
                <Chip
                  key={day.key}
                  label={day.label}
                  onClick={() => toggleDay(day.key)}
                  variant={days.includes(day.key) ? 'filled' : 'outlined'}
                  color={days.includes(day.key) ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Grid>

          {/* Tasks */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Tasks</Typography>
            {tasks.map((task, index) => (
              <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 24, color: 'text.secondary' }}>{index + 1}.</Typography>
                <TextField
                  value={task.name}
                  onChange={(e) => updateTaskName(task.id, e.target.value)}
                  placeholder={`Task ${index + 1}`}
                  size="small"
                  fullWidth
                />
                <IconButton size="small" onClick={() => removeTask(task.id)} disabled={tasks.length <= 1}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={addTask}>Add Task</Button>
          </Grid>

          <Grid size={12}>
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Assigned To</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberList.map((member) => {
                const isSelected = assignedTo.includes(member.id);
                const memberName = getMemberName(member.id);
                const color = getMemberColor(member.id);
                return (
                  <Chip
                    key={member.id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{memberName.charAt(0)}</Avatar>}
                    label={memberName}
                    onClick={() => toggleMember(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Reward Form Dialog
interface RewardData {
  name: string;
  description?: string;
  pointsCost: number;
  availableTo?: string[];
}

interface RewardFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: RewardData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  reward?: { id: string; data?: { data?: RewardData } } | null;
}

export const RewardFormDialog: React.FC<RewardFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  reward,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!reward;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pointsCost, setPointsCost] = useState('');
  const [availableTo, setAvailableTo] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (reward) {
        const data = (reward.data?.data || {}) as any;
        setName(data.name || '');
        setDescription(data.description || '');
        setPointsCost(data.pointsCost?.toString() || '');
        setAvailableTo(data.availableTo || []);
      } else {
        setName('');
        setDescription('');
        setPointsCost('');
        setAvailableTo([]);
      }
      setError(null);
    }
  }, [open, reward]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Reward name is required');
      return;
    }
    const points = parseInt(pointsCost);
    if (isNaN(points) || points <= 0) {
      setError('Please enter a valid points cost');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || undefined,
        pointsCost: points,
        availableTo: availableTo.length > 0 ? availableTo : undefined,
      }, selectedFamilyIds);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save reward');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete reward');
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (memberId: string) => {
    setAvailableTo((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Reward' : 'New Reward'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField label="Reward Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus placeholder="e.g., Extra Screen Time, Ice Cream Trip" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={12}>
            <TextField label="Points Cost" value={pointsCost} onChange={(e) => setPointsCost(e.target.value)} fullWidth required type="number" placeholder="Points needed to redeem" />
          </Grid>

          <Grid size={12}>
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Available To</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberList.map((member) => {
                const isSelected = availableTo.includes(member.id);
                const memberName = getMemberName(member.id);
                const color = getMemberColor(member.id);
                return (
                  <Chip
                    key={member.id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{memberName.charAt(0)}</Avatar>}
                    label={memberName}
                    onClick={() => toggleMember(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
            {availableTo.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Leave empty to make available to all family members
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default {
  ChoreFormDialog,
  RoutineFormDialog,
  RewardFormDialog,
};
